import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type {
  ProjectSiteBoardReading,
  ProjectSiteLogKind,
  ProjectSiteProductGroup,
  ProjectSiteReporterKind,
  ProjectSiteStage,
  ProjectSiteStatus,
} from '@mt/shared';
import { PROJECT_SITE_VIDEO_BYTES } from '@mt/shared';
import { api } from '@/lib/api';
import type { Paged } from '@/lib/types';
import { prepareReportPhoto } from '@/lib/report-photo';

/** CR-016 Báo dự án công trình (decisions/CR-016-bao-du-an-cong-trinh.md). */
export const SITES_KEY = ['project-sites'] as const;

export interface ProjectSite {
  id: number;
  code: string;
  name: string | null;
  title: string;
  address: string | null;
  lat: number | null;
  lng: number | null;
  gpsAccuracyM: number | null;
  stage: ProjectSiteStage;
  productGroups: ProjectSiteProductGroup[];
  investor: string | null;
  mainContractor: string | null;
  contactName: string | null;
  contactPhone: string | null;
  note: string | null;
  status: ProjectSiteStatus;
  reporterKind: ProjectSiteReporterKind;
  reportedBy: number;
  reporterName: string;
  assignedTo: number | null;
  assignedName: string | null;
  assignedAt: string | null;
  mergedIntoCode: string | null;
  photoCount: number;
  videoCount: number;
  coverMediaId: number | null;
  leadId: number | null;
  leadCode: string | null;
  customerId: number | null;
  customerCode: string | null;
  customerName: string | null;
  lostReason: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface SiteMedia {
  id: number;
  kind: 'photo' | 'video';
  mime: string;
  durationS: number | null;
  bytes: number;
  lat: number | null;
  lng: number | null;
  takenAt: string | null;
  createdAt: string;
}
export interface NearbySite {
  id: number;
  code: string;
  title: string;
  status: ProjectSiteStatus;
  distanceM: number;
}
export interface ProjectSiteDetail extends ProjectSite {
  media: SiteMedia[];
  logs: { id: number; kind: ProjectSiteLogKind; kindLabel: string; note: string; createdByName: string; createdAt: string }[];
  nearby: NearbySite[];
  merged: { id: number; code: string; reporterName: string; createdAt: string }[];
}
export interface MapSite {
  id: number;
  code: string;
  title: string;
  lat: number;
  lng: number;
  stage: ProjectSiteStage;
  status: ProjectSiteStatus;
  reporterKind: ProjectSiteReporterKind;
  createdAt: string;
}

export interface SiteFilter {
  q?: string;
  status?: ProjectSiteStatus[];
  stage?: ProjectSiteStage | '';
  reporterKind?: ProjectSiteReporterKind | '';
  mine?: boolean;
  assignedToMe?: boolean;
  /** YYYY-MM-DD giờ VN, [từ, đến) */
  createdFrom?: string;
  createdTo?: string;
  page?: number;
  pageSize?: number;
}
const params = (f: SiteFilter) => ({
  q: f.q || undefined,
  status: f.status?.length ? f.status.join(',') : undefined,
  stage: f.stage || undefined,
  reporterKind: f.reporterKind || undefined,
  mine: f.mine ? 'true' : undefined,
  assignedToMe: f.assignedToMe ? 'true' : undefined,
  createdFrom: f.createdFrom,
  createdTo: f.createdTo,
  page: f.page,
  pageSize: f.pageSize,
});

export const useSites = (f: SiteFilter, enabled = true) =>
  useQuery({
    queryKey: [...SITES_KEY, 'list', f],
    queryFn: () => api.get<Paged<ProjectSite>>('/project-sites', params(f)),
    placeholderData: keepPreviousData,
    enabled,
  });
export const useSiteMap = (f: SiteFilter, enabled = true) =>
  useQuery({
    queryKey: [...SITES_KEY, 'map', f],
    queryFn: () => api.get<MapSite[]>('/project-sites/map', params(f)),
    enabled,
    placeholderData: keepPreviousData,
  });
export const useSite = (id: number | null) =>
  useQuery({ queryKey: [...SITES_KEY, 'one', id], queryFn: () => api.get<ProjectSiteDetail>(`/project-sites/${id}`), enabled: !!id });
export const useNearby = (pos: { lat: number; lng: number } | null) =>
  useQuery({
    // Làm tròn ~10 m để không gọi lại mỗi lần GPS nhích
    queryKey: [...SITES_KEY, 'nearby', pos && Math.round(pos.lat * 1e4), pos && Math.round(pos.lng * 1e4)],
    queryFn: () => api.get<{ count: number; nearestM: number | null; items: NearbySite[] }>('/project-sites/nearby', { lat: pos!.lat, lng: pos!.lng }),
    enabled: !!pos,
    staleTime: 60_000,
  });

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: SITES_KEY });
}
const siteMutation = <B>(fn: (b: B) => Promise<ProjectSiteDetail>) =>
  function useIt() {
    const inv = useInvalidate();
    return useMutation({ mutationFn: fn, onSuccess: inv });
  };

export type SiteInput = Partial<
  Pick<
    ProjectSite,
    'name' | 'address' | 'lat' | 'lng' | 'gpsAccuracyM' | 'stage' | 'productGroups' | 'investor' | 'mainContractor' | 'contactName' | 'contactPhone' | 'note'
  >
>;
export const useUpdateSite = siteMutation(({ id, ...b }: SiteInput & { id: number; version: number }) =>
  api.patch<ProjectSiteDetail>(`/project-sites/${id}`, b),
);
export const useClaimSite = siteMutation(({ id, version }: { id: number; version: number }) =>
  api.post<ProjectSiteDetail>(`/project-sites/${id}/claim`, { version }),
);
export const useSiteLog = siteMutation(({ id, ...b }: { id: number; kind: 'call' | 'visit' | 'note'; note: string }) =>
  api.post<ProjectSiteDetail>(`/project-sites/${id}/logs`, b),
);
export const useSiteStatus = siteMutation(({ id, ...b }: { id: number; status: ProjectSiteStatus; lostReason?: string | null; version: number }) =>
  api.post<ProjectSiteDetail>(`/project-sites/${id}/status`, b),
);
export const useMergeSite = siteMutation(({ id, ...b }: { id: number; intoId: number; version: number }) =>
  api.post<ProjectSiteDetail>(`/project-sites/${id}/merge`, b),
);
export const useSiteLead = siteMutation(({ id, ...b }: { id: number; acknowledgeDuplicates?: boolean }) =>
  api.post<ProjectSiteDetail>(`/project-sites/${id}/lead`, b),
);

// ---------------------------------------------------------------------------
// Báo dự án: tạo → tải từng ảnh / video → gửi (báo KD thị trường)
// ---------------------------------------------------------------------------
export interface PendingPhoto {
  key: string;
  kind: 'photo';
  data: string;
  thumb: string;
  preview: string;
  lat: number | null;
  lng: number | null;
  takenAt: string | null;
}
export interface PendingVideo {
  key: string;
  kind: 'video';
  blob: Blob;
  thumb: string | null;
  preview: string;
  durationS: number | null;
  lat: number | null;
  lng: number | null;
  takenAt: string | null;
}
export type PendingMedia = (PendingPhoto | PendingVideo) & { uploadedId?: number };

export async function photoFromFile(file: File, pos: { lat: number; lng: number } | null): Promise<PendingPhoto> {
  const { data, thumb } = await prepareReportPhoto(file, { pos: pos ? { ...pos, accuracyM: null } : null });
  return {
    key: `${file.name}-${file.size}-${file.lastModified}`,
    kind: 'photo',
    data,
    thumb,
    preview: `data:image/jpeg;base64,${thumb}`,
    lat: pos?.lat ?? null,
    lng: pos?.lng ?? null,
    takenAt: file.lastModified ? new Date(file.lastModified).toISOString() : null,
  };
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  let bin = '';
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(bin);
}

/** Ảnh bìa + thời lượng của video (khung hình ở giây thứ 1). Trình duyệt không đọc được thì bỏ qua ảnh bìa. */
async function videoPoster(blob: Blob): Promise<{ thumb: string | null; durationS: number | null }> {
  const url = URL.createObjectURL(blob);
  try {
    const v = document.createElement('video');
    v.muted = true;
    v.playsInline = true;
    v.preload = 'auto';
    v.src = url;
    await new Promise<void>((ok, fail) => {
      v.onloadeddata = () => ok();
      v.onerror = () => fail(new Error('video'));
      setTimeout(() => fail(new Error('timeout')), 5000);
    });
    const durationS = Number.isFinite(v.duration) ? Math.round(v.duration * 10) / 10 : null;
    v.currentTime = Math.min(1, (durationS ?? 1) / 2);
    await new Promise<void>((ok) => {
      v.onseeked = () => ok();
      setTimeout(ok, 1500);
    });
    const ratio = Math.min(1, 400 / Math.max(v.videoWidth || 400, v.videoHeight || 400));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round((v.videoWidth || 400) * ratio));
    c.height = Math.max(1, Math.round((v.videoHeight || 300) * ratio));
    c.getContext('2d')!.drawImage(v, 0, 0, c.width, c.height);
    const jpg = await new Promise<Blob | null>((r) => c.toBlob(r, 'image/jpeg', 0.8));
    return { thumb: jpg ? await blobToBase64(jpg) : null, durationS };
  } catch {
    return { thumb: null, durationS: null };
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function videoFromBlob(blob: Blob, pos: { lat: number; lng: number } | null, durationS?: number): Promise<PendingVideo> {
  if (blob.size > PROJECT_SITE_VIDEO_BYTES) throw new Error('Video quá 8 MB. Quay bằng nút "Quay video" trong ứng dụng (tự giới hạn 30 giây).');
  const poster = await videoPoster(blob);
  return {
    key: `video-${blob.size}-${Date.now()}`,
    kind: 'video',
    blob,
    thumb: poster.thumb,
    preview: poster.thumb ? `data:image/jpeg;base64,${poster.thumb}` : '',
    durationS: durationS ?? poster.durationS,
    lat: pos?.lat ?? null,
    lng: pos?.lng ?? null,
    takenAt: new Date().toISOString(),
  };
}

export async function createSite(body: SiteInput) {
  return api.post<ProjectSiteDetail>('/project-sites', body);
}
export async function uploadMedia(siteId: number, m: PendingMedia) {
  const body =
    m.kind === 'photo'
      ? { kind: 'photo', data: m.data, thumb: m.thumb, lat: m.lat, lng: m.lng, takenAt: m.takenAt }
      : { kind: 'video', data: await blobToBase64(m.blob), thumb: m.thumb, durationS: m.durationS, lat: m.lat, lng: m.lng, takenAt: m.takenAt };
  return api.post<{ id: number }>(`/project-sites/${siteId}/media`, body);
}
export async function submitSite(siteId: number) {
  return api.post<ProjectSiteDetail>(`/project-sites/${siteId}/submit`);
}
export async function deleteMedia(siteId: number, mediaId: number) {
  return api.del(`/project-sites/${siteId}/media/${mediaId}`);
}

/** Ảnh / video cần token → tải blob rồi tạo object URL. */
export function useSiteMediaSrc(siteId: number, mediaId: number | null, size: 'thumb' | 'full' = 'thumb') {
  const q = useQuery({
    queryKey: ['project-site-media', siteId, mediaId, size],
    queryFn: () => api.blob(`/project-sites/${siteId}/media/${mediaId}/file`, { size }),
    enabled: !!mediaId,
    staleTime: Infinity,
    gcTime: 10 * 60_000,
  });
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!q.data) return;
    const u = URL.createObjectURL(q.data);
    setSrc(u);
    return () => URL.revokeObjectURL(u);
  }, [q.data]);
  return src;
}

/** Link chỉ đường Google Maps. */
export const directionsUrl = (s: { lat: number | null; lng: number | null; address: string | null }) =>
  s.lat !== null && s.lng !== null
    ? `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address ?? '')}`;

/** CR-016 B2 (DEC-266): AI đọc ảnh bảng thông tin dự án → gợi ý (không lưu). */
export const useReadBoard = () => useMutation({ mutationFn: (id: number) => api.post<ProjectSiteBoardReading>(`/project-sites/${id}/read-board`) });

export interface ReporterStat {
  userId: number;
  fullName: string;
  reporterKind: ProjectSiteReporterKind;
  total: number;
  approaching: number;
  won: number;
  lost: number;
  duplicate: number;
}
export const useReporterStats = (f: { from?: string; to?: string }, enabled = true) =>
  useQuery({ queryKey: [...SITES_KEY, 'reporters', f], queryFn: () => api.get<ReporterStat[]>('/project-sites/reporters', f), enabled });
